import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Header } from "@/components/header";
import { Footer } from '@/components/footer';
import { CTA } from '@/components/cta';
import { FAQ } from '@/components/faq';

export default function AutomaticUpdatesBenefit() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-16">        
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-slate-100 dark:bg-slate-900/30 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-600 dark:text-slate-400">
              <RefreshCw className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t('benefitsCloud.benefits.3.title')}
            </h1>
          </div>
          
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
              {t('benefitsCloud.benefits.3.description')}
            </p>
            
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                {t('benefitsCloud.details.automaticUpdates.title', 'Always Up-to-Date Security')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                {t('benefitsCloud.details.automaticUpdates.description', 'Our cloud-based security system ensures you always have the latest features and security patches without any manual intervention, keeping your protection current against emerging threats.')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.automaticUpdates.benefit1', 'Seamless Updates')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.automaticUpdates.benefit1Desc', 'Updates are applied automatically with no downtime or disruption to your security.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.automaticUpdates.benefit2', 'Latest Security Patches')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.automaticUpdates.benefit2Desc', 'Stay protected against the latest threats with automatic security updates.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.automaticUpdates.benefit3', 'New Features')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.automaticUpdates.benefit3Desc', 'Get access to the latest features and improvements as soon as they\'re released.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.automaticUpdates.benefit4', 'No IT Overhead')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.automaticUpdates.benefit4Desc', 'Eliminate the need for manual updates and maintenance.')}
                  </p>
                </div>
              </div>
              
              <div className="mt-16">
                <FAQ 
                    faqs={t('benefitsCloud.details.automaticUpdates.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} 
                    title={t('faq.title', 'Frequently Asked Questions')}
                    subtitle={t('benefitsCloud.details.automaticUpdates.faqSubtitle', 'Everything you need to know about automatic updates')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <CTA />
      <Footer />
    </div>
  );
}
